import type { AvailabilitySlot } from '@/lib/types/availability';
import type { CustomField } from '@/lib/types/customField';
import type { MaintenanceTimeSpan } from '@/lib/types/maintenance';
import type { Reservation, ReservationResource, ReservationResourceCustomField } from '@/lib/types/reservation';
import { ReservationStatus } from '@/lib/types/reservation';
import { useAvailabilitySlotsStore } from '@/store/availability';
import { useMaintenancesStore } from '@/store/maintenance';
import { useReservationsStore } from '@/store/reservation';
import { useResourcesStore } from '@/store/resource';
import { dayOfWeekMatches, expandDayOfWeek, getDayOfWeekFromDate, msToResourceLocalTimeString, msToResourceLocalYmd, parseDateTime, resourceLocalDateTimeToMs, resourceLocalDayBoundsMs } from '@/utils/date';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

export interface EditingSlot {
    resourceId: string;
    startMs: number;
    endMs: number;
    customFields: ReservationResourceCustomField[];
    startDate: Date | null;
    startTime: string | null;
    endTime: string | null;
    // The resource's IANA timezone id (D-12 / TZ-02). This is the seam that threads the resource's
    // clock into every slotDialog.* call at once: parseTimeString, both fetch windows and both
    // timeline block builders read it, so a caller cannot put half the timeline on one clock and
    // half on another. Nullable because a resource whose stored id resolves to no served entry keeps
    // working on browser-local time rather than going offline over a data glitch (D-09) — `null` IS
    // the browser-local-fallback test, and every utils/date.ts helper applies that fallback itself.
    timezoneId: string | null;
}

export interface TimelineBlock {
    id?: string;
    title: string;
    startTime: string;
    endTime: string;
    left: number;
    width: number;
    // Maintenance blocks carry their own description (TLINE-01). Optional because reservation blocks have none,
    // nullable because MaintenanceTimeSpan.description is itself string | null and D-06 omits the row rather than
    // substituting a placeholder.
    description?: string | null;
    // The cap of a partially-blocking maintenance (D-09). Same shape and same reasoning as description above:
    // optional because reservation blocks have none, nullable because MaintenanceTimeSpan.reducedCapacity is itself
    // number | null and a fully-blocking maintenance legitimately has no cap. `!= null` is therefore the partial test.
    reducedCapacity?: number | null;
}

export function useSlotDialog() {
    const { t } = useI18n();

    // Stores
    const resourcesStore = useResourcesStore();
    const availabilitySlotsStore = useAvailabilitySlotsStore();
    const maintenancesStore = useMaintenancesStore();
    const reservationsStore = useReservationsStore();

    // State
    const timelineLoading = ref(false);
    const timelineReservations = ref<Reservation[]>([]);
    const timelineMaintenanceTimeSpans = ref<MaintenanceTimeSpan[]>([]);
    const validationError = ref<string | null>(null);
    const isValidating = ref(false);

    // Get current resource ID from editing slot
    const getCurrentResourceId = (editingSlot: EditingSlot, hasVisitors: boolean, selectedResourceId: string | null) => {
        return !hasVisitors ? selectedResourceId : editingSlot.resourceId;
    };

    // Get availability slot for selected date's day of week
    const getSelectedDayAvailability = (editingSlot: EditingSlot, resourceId: string | null): AvailabilitySlot | null => {
        if (!editingSlot.startDate || !resourceId) return null;

        const resourceAvailability = availabilitySlotsStore.getSlotsByResourceId(resourceId);
        if (!resourceAvailability?.slots?.length) return null;

        const selectedDayName = getDayOfWeekFromDate(editingSlot.startDate);
        return resourceAvailability.slots.find(slot => slot.dayOfWeek === selectedDayName)
            ?? resourceAvailability.slots.find(slot => dayOfWeekMatches(slot.dayOfWeek, selectedDayName))
            ?? null;
    };

    // Get timeline bounds based on availability
    const getTimelineBounds = (availability: AvailabilitySlot | null): { start: string; end: string } | null => {
        if (!availability) return null;
        return { start: availability.start, end: availability.end };
    };

    // Get days of week that have availability configured for resource
    const getAvailableDaysOfWeek = (resourceId: string | null): Set<string> => {
        if (!resourceId) return new Set();

        const resourceAvailability = availabilitySlotsStore.getSlotsByResourceId(resourceId);
        if (!resourceAvailability?.slots?.length) return new Set();

        return new Set(resourceAvailability.slots.flatMap(slot => expandDayOfWeek(slot.dayOfWeek)));
    };

    // Fetch availability data for resource (maintenances no longer fetched here)
    async function fetchResourceTimelineData(resourceId: string) {
        try {
            await availabilitySlotsStore.fetchByResourceId(resourceId);
        } catch (err) {
            console.error('Error fetching resource timeline data:', err);
        }
    }

    // Fetch reservations for a specific date.
    //
    // D-12: the query window is the RESOURCE's day, not the browser's. Rendering resource-local over
    // a browser-local window produces a day that is partly empty and partly bleeding in from the next
    // day, so SC3 is not satisfiable by a render-only change. Fetching plus-or-minus one day and
    // filtering client-side was rejected: it is roughly triple the payload on every timeline open.
    // resourceLocalDayBoundsMs falls back to the browser's own boundaries when the zone cannot be
    // resolved (D-09), so a null id reproduces today's exact behaviour.
    //
    // The window is pushed down as `yyyy-MM-dd` CALENDAR-DATE STRINGS rendered on the resource's
    // clock, matching the sibling fetchDateMaintenances. It used to pass the bound INSTANTS as
    // `Date`s, which fetchReservations then re-read with the browser-local formatDateToyyyMMdd —
    // undoing the conversion and, whenever the two zones straddle midnight, widening or shifting the
    // requested span by a whole day. getTimelineReservationBlocks does not re-filter by day, so
    // those adjacent-day reservations rendered on the selected day's timeline and fed the overlap
    // check. Never let an instant cross a browser-local formatter on this path.
    async function fetchDateReservations(resourceId: string, date: Date, timeZoneId: string | null) {
        timelineLoading.value = true;

        try {
            const bounds = resourceLocalDayBoundsMs(timeZoneId, date);
            if (!bounds) {
                timelineReservations.value = [];
                return;
            }
            // `to` was inclusive of the same calendar date before this change; preserved.
            const ymd = msToResourceLocalYmd(timeZoneId, bounds.startMs);

            await reservationsStore.fetchByDates(
                ymd,
                ymd,
                undefined,
                undefined,
                resourcesStore.resources.filter(r => r.id === resourceId),
                undefined,
                false
            );

            timelineReservations.value = reservationsStore.reservations.filter((res: { slots: ReservationResource[]; }) => {
                return res.slots.some((slot: ReservationResource) => slot.resourceId === resourceId);
            });
        } catch (err) {
            console.error('Error fetching date reservations:', err);
            timelineReservations.value = [];
        } finally {
            timelineLoading.value = false;
        }
    }

    // Fetch maintenance timespans for a specific date and resource.
    //
    // Shift a `yyyy-MM-dd` calendar date by whole days. Deliberately UTC arithmetic on a bare
    // calendar date: there is no zone and no DST here because the value is a DATE, not an instant.
    function shiftYmd(ymd: string, days: number): string {
        const [y, m, d] = ymd.split('-').map(Number);
        const shifted = new Date(Date.UTC(y, m - 1, d + days));
        return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}`;
    }

    // D-12. `from`/`to` are NOT interpreted in the resource's zone by the backend — an earlier
    // comment here claimed they were, and that claim was false. ResourceSlotService.ComputeSpans's
    // DateOnly overload does `fromUtc.Value.ToDateTime(new TimeOnly(0, 0), DateTimeKind.Utc)`: the
    // parameters are UTC midnights, and ExpandSlotToSpans clamps every emitted span against them.
    // For a resource at +13 the selected local day is [D-1 11:00Z, D 11:00Z] while the query window
    // is [D 00:00Z, D+1 00:00Z], so the first 13 hours of the resource's day return no spans at all
    // — an empty morning over a genuine maintenance window, with the local overlap check passing and
    // the operator rejected at submit instead.
    //
    // The window is therefore widened by one calendar day on each side and narrowed back to the
    // resource's own day by getTimelineMaintenanceBlocks, whose clamp already uses exactly the same
    // resourceLocalDayBoundsMs call. One day either side covers every real zone (max |offset| is
    // 14 h). The cost is a handful of extra spans for ONE resource, not the ~3x whole-calendar
    // payload D-12 rejected. Fixing this properly means giving the per-resource `timespans` routes
    // an epoch-ms window server-side; until then this is a mitigation, not the fix.
    //
    // `to` is EXCLUSIVE server-side (the clamp is Min(endUtc, toUtc) against toUtc's UTC midnight),
    // so covering the selected local day D for every real offset needs [D-1, D+2): a resource at
    // +14 starts its day at D-1 10:00Z and one at -12 ends its day at D+1 12:00Z. D+1 as the upper
    // bound — the incumbent value — silently truncates every negative-offset resource.
    //
    // The shift is whole-day CALENDAR arithmetic on the resource-local date, not milliseconds added
    // to an instant. A resource-local day is 23 or 25 hours long on a DST transition date, so a flat
    // +86_400_000 collapses the window twice a year.
    async function fetchDateMaintenances(resourceId: string, date: Date, timeZoneId: string | null) {
        try {
            const bounds = resourceLocalDayBoundsMs(timeZoneId, date);
            if (!bounds) {
                timelineMaintenanceTimeSpans.value = [];
                return;
            }
            const selectedYmd = msToResourceLocalYmd(timeZoneId, bounds.startMs);
            const from = shiftYmd(selectedYmd, -1);
            const to = shiftYmd(selectedYmd, 2);

            await maintenancesStore.fetchTimeSpans(from, to, resourceId);

            timelineMaintenanceTimeSpans.value = maintenancesStore.timespans.filter(
                ts => ts.resourceId === resourceId
            );
        } catch (err) {
            console.error('Error fetching date maintenances:', err);
            timelineMaintenanceTimeSpans.value = [];
        }
    }

    // Convert HH:mm time string to percentage position on timeline
    function timeToPercent(time: string, timelineBounds: { start: string; end: string } | null): number {
        if (!timelineBounds) return 0;

        const [h, m] = time.split(':').map(Number);
        const totalMinutes = h * 60 + m;

        const [startH, startM] = timelineBounds.start.split(':').map(Number);
        const [endH, endM] = timelineBounds.end.split(':').map(Number);
        const startMinutes = startH * 60 + startM;
        const endMinutes = endH * 60 + endM;
        const rangeMinutes = endMinutes - startMinutes;

        if (rangeMinutes <= 0) return 0;

        return Math.max(0, Math.min(100, ((totalMinutes - startMinutes) / rangeMinutes) * 100));
    }

    // Get reservations for timeline display with position info
    const getTimelineReservationBlocks = (
        editingSlot: EditingSlot,
        resourceId: string | null,
        timelineBounds: { start: string; end: string } | null,
        excludeReservationId: string | null = null
    ): TimelineBlock[] => {
        if (!editingSlot.startDate || !resourceId) return [];

        return timelineReservations.value
            .filter((res: Reservation) =>
                res.status !== ReservationStatus.Canceled &&
                res.id !== excludeReservationId
            )
            .flatMap((res: { slots: any[]; id: any; title: any; }) => {
                return res.slots
                    .filter((slot: ReservationResource) => slot.resourceId === resourceId)
                    .map((slot: ReservationResource) => {
                        // TZ-02: the block's label and its position are both derived from the resource's
                        // clock. This is the only place the conversion belongs — timeToPercent and the
                        // whole downstream renderer are "HH:mm"-string based, so converting once here
                        // puts every hour marker, block and label on one clock by construction.
                        const startTime = msToResourceLocalTimeString(editingSlot.timezoneId, slot.startMs);
                        const endTime = msToResourceLocalTimeString(editingSlot.timezoneId, slot.endMs);

                        return {
                            id: res.id,
                            title: res.title,
                            startTime,
                            endTime,
                            left: timeToPercent(startTime, timelineBounds),
                            width: timeToPercent(endTime, timelineBounds) - timeToPercent(startTime, timelineBounds)
                        };
                    });
            });
    };

    // Get maintenance blocks for timeline display from MaintenanceTimeSpan[]
    const getTimelineMaintenanceBlocks = (
        editingSlot: EditingSlot,
        timelineBounds: { start: string; end: string } | null
    ): TimelineBlock[] => {
        if (!editingSlot.startDate || !timelineBounds) return [];

        // D-12: the clamp bounds MUST be the same resource-local day the fetch window used, or a
        // block that was fetched inside the window gets clipped outside it.
        const bounds = resourceLocalDayBoundsMs(editingSlot.timezoneId, editingSlot.startDate);
        if (!bounds) return [];
        const dayStart = new Date(bounds.startMs);
        const dayEnd = new Date(bounds.endMs);

        return timelineMaintenanceTimeSpans.value
            .filter(ts => {
                // Only include timespans that overlap with the selected date.
                // startUtc/endUtc arrive as 'yyyy-MM-dd HH:mm:ss' with no zone marker, which V8 parses as LOCAL
                // time — parseDateTime is the single shared helper that reads them as UTC (same call the calendar makes).
                const tsStart = parseDateTime(ts.startUtc);
                const tsEnd = parseDateTime(ts.endUtc);
                return tsStart < dayEnd && tsEnd > dayStart;
            })
            .map(ts => {
                const tsStart = parseDateTime(ts.startUtc);
                const tsEnd = parseDateTime(ts.endUtc);

                // Clamp to the selected day's boundaries
                const effectiveStart = tsStart < dayStart ? dayStart : tsStart;
                const effectiveEnd = tsEnd > dayEnd ? dayEnd : tsEnd;

                // TZ-02: same conversion as the reservation blocks above — the clamped instants are
                // rendered on the resource's clock, not the browser's.
                const startTime = msToResourceLocalTimeString(editingSlot.timezoneId, effectiveStart.getTime());
                const endTime = msToResourceLocalTimeString(editingSlot.timezoneId, effectiveEnd.getTime());

                // If maintenance spans the entire day, clamp to timeline bounds
                const finalStartTime = effectiveStart <= dayStart ? timelineBounds.start : startTime;
                const finalEndTime = effectiveEnd >= dayEnd ? timelineBounds.end : endTime;

                return {
                    title: ts.title,
                    description: ts.description,
                    reducedCapacity: ts.reducedCapacity,
                    startTime: finalStartTime,
                    endTime: finalEndTime,
                    left: timeToPercent(finalStartTime, timelineBounds),
                    width: timeToPercent(finalEndTime, timelineBounds) - timeToPercent(finalStartTime, timelineBounds)
                };
            });
    };

    // Get custom fields for resource
    const getCustomFields = (resourceId: string | null): CustomField[] => {
        if (!resourceId) return [];

        const resource = resourcesStore.resources.find(r => r.id === resourceId);
        if (!resource || !resource.customFields) return [];

        return resource.customFields.filter(field => field.enabled);
    };

    // Get resource info
    const getResourceInfo = (resourceId: string | null) => {
        const resource = resourcesStore.resources.find(r => r.id === resourceId);
        return {
            name: resource?.name || t('calendar.new.resourcesSlots.resourceNotSelected'),
            location: resource?.location || ''
        };
    };

    // Parse a date + "HH:mm" into the instant it denotes on the RESOURCE's clock (TZ-02).
    //
    // This is the core seam of the phase: every timeline and wizard time flows through it, so the
    // conversion is made once here rather than at each call site.
    //
    // `timeZoneId` is REQUIRED and deliberately has no default. It used to default to null, which
    // was described as "a widening change, not a breaking one" — but null is also the legitimate
    // "degraded / unresolvable zone" value, so a FORGOTTEN argument and a DELIBERATE browser-local
    // fallback were indistinguishable at the call site and under review. Three sites in
    // NewReservation.vue silently kept the default, one of which was a genuine defect (the capacity
    // pre-check). Making the parameter required means the compiler, not a reviewer, finds the next
    // one. Pass an explicit null when browser-local really is intended.
    function parseTimeString(date: Date | null, time: string | null, timeZoneId: string | null): Date | null {
        const ms = resourceLocalDateTimeToMs(timeZoneId, date, time);
        if (ms === null) return null;
        return new Date(ms);
    }

    // Calculate duration between two dates
    const calculateDuration = (start: Date, end: Date): string => {
        const diff = end.getTime() - start.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

        if (hours > 0 && minutes > 0) {
            return `${hours}h ${minutes}m`;
        } else if (hours > 0) {
            return `${hours}h`;
        } else if (minutes > 0) {
            return `${minutes}m`;
        } else {
            return '0m';
        }
    };

    // Validation
    const createValidation = (
        editingSlot: EditingSlot,
        hasVisitors: boolean,
        selectedResourceId: string | null,
        timelineReservationBlocks: TimelineBlock[],
        timelineMaintenanceBlocks: TimelineBlock[]
    ) => {
        const currentResourceId = getCurrentResourceId(editingSlot, hasVisitors, selectedResourceId);
        // TZ-02: same rule as ResourceSlotCard's inline validation — the entered time and the block
        // times it is compared against are both resolved on the resource's clock, or the overlap test
        // silently compares two different clocks.
        const computedStartDateTime = parseTimeString(editingSlot.startDate, editingSlot.startTime, editingSlot.timezoneId);
        const computedEndDateTime = parseTimeString(editingSlot.startDate, editingSlot.endTime, editingSlot.timezoneId);

        return {
            resource: computed(() => ({
                invalid: hasVisitors && !editingSlot.resourceId,
                message: t('calendar.new.resourcesSlots.addSlot.validation.resourceRequired')
            })),
            startDate: computed(() => ({
                invalid: !editingSlot.startDate,
                message: t('calendar.new.resourcesSlots.addSlot.validation.dateRequired')
            })),
            startTime: computed(() => ({
                invalid: !editingSlot.startTime,
                message: t('calendar.new.resourcesSlots.addSlot.validation.timeRequired')
            })),
            endTime: computed(() => ({
                invalid: !editingSlot.endTime,
                message: t('calendar.new.resourcesSlots.addSlot.validation.timeRequired')
            })),
            dateTimeOrder: computed(() => {
                const start = computedStartDateTime;
                const end = computedEndDateTime;
                if (!start || !end) return { invalid: false, message: '' };
                return {
                    invalid: start >= end,
                    message: t('calendar.new.resourcesSlots.addSlot.validation.endAfterStart')
                };
            }),
            overlapsReservation: computed(() => {
                const start = computedStartDateTime;
                const end = computedEndDateTime;
                if (!start || !end || start >= end) return { invalid: false, message: '' };

                const startMs = start.getTime();
                const endMs = end.getTime();

                const hasOverlap = timelineReservationBlocks.some(block => {
                    const blockStartMs = parseTimeString(editingSlot.startDate, block.startTime, editingSlot.timezoneId)?.getTime() || 0;
                    const blockEndMs = parseTimeString(editingSlot.startDate, block.endTime, editingSlot.timezoneId)?.getTime() || 0;
                    return !(endMs <= blockStartMs || startMs >= blockEndMs);
                });

                return {
                    invalid: hasOverlap,
                    message: t('calendar.new.resourcesSlots.addSlot.validation.overlapsReservation')
                };
            }),
            overlapsMaintenance: computed(() => {
                const start = computedStartDateTime;
                const end = computedEndDateTime;
                if (!start || !end || start >= end) return { invalid: false, message: '' };

                const startMs = start.getTime();
                const endMs = end.getTime();

                const hasOverlap = timelineMaintenanceBlocks.some(block => {
                    // Mirrors ResourceSlotCard: a partially-blocking maintenance caps the window rather than
                    // barring it, and the capacity badge reports exceeding that cap. Fully-blocking spans carry
                    // reducedCapacity null and still block.
                    if (block.reducedCapacity != null) return false;

                    const blockStartMs = parseTimeString(editingSlot.startDate, block.startTime, editingSlot.timezoneId)?.getTime() || 0;
                    const blockEndMs = parseTimeString(editingSlot.startDate, block.endTime, editingSlot.timezoneId)?.getTime() || 0;
                    return !(endMs <= blockStartMs || startMs >= blockEndMs);
                });

                return {
                    invalid: hasOverlap,
                    message: t('calendar.new.resourcesSlots.addSlot.validation.overlapsMaintenance')
                };
            })
        };
    };

    return {
        // State
        timelineLoading,
        timelineReservations,
        timelineMaintenanceTimeSpans,
        validationError,
        isValidating,

        // Stores
        resourcesStore,

        // Functions
        getCurrentResourceId,
        getSelectedDayAvailability,
        getTimelineBounds,
        getAvailableDaysOfWeek,
        fetchResourceTimelineData,
        fetchDateReservations,
        fetchDateMaintenances,
        timeToPercent,
        getTimelineReservationBlocks,
        getTimelineMaintenanceBlocks,
        getCustomFields,
        getResourceInfo,
        parseTimeString,
        calculateDuration,
        createValidation
    };
}
